'use client';

import { ReadonlyURLSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { ProductsItem } from '@app-types/api/statics';
import { EligibilityConfigType } from '@app-types/config';
import {
    EligibilityError,
    IRudiApiRechercherAdresse,
    ShortListContext,
    WetoStepChangeEvent,
} from '@app-types/eligibility';
import { UpsellModalComponentType } from '@app-types/eligibility/config';
import { FaiType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { UpsellModalContainerProps } from '@components/fai-upsell-modal';

import { useMarketings } from '@hooks/use-marketings';

import { emitPriceContextChangeEvent } from '@helpers/marketings';

import { ELIGIBILITY_STEP_NAME, ELIGIBILITY_STEP_STATUS, TYPE_ELIGIBILITY } from '@constants/eligibility';
import { MarketingType } from '@constants/plan';
import { PLAY, TECHNOLOGY } from '@constants/provider';

import { EligibilityAddress } from './eligibility-address';
import { EligibilityOffer } from './eligibility-offer';
import { EligibilityResults } from './eligibility-results';
import { EligibilityResultType } from './eligibility-widget';
import { JourneyConfigType } from './journey-config';
import { getShortListName, splitShortListParams } from './short-list';
import { normalizeTechnology } from './technology';
import { sendUpsellModalClick, sendUpsellModalClose, sendUpsellModalOpen } from './tracking';

/**
 * Hook to manage the eligibility state.
 * Should be used in one file (does not use context).
 */

export function useEligibility(
    eligibilityConfig: EligibilityConfigType,
    journeyConfig: JourneyConfigType,
    addToCart: (gencodes: string[], redirect?: boolean, callback?: () => void) => Promise<void>,
    searchParams: ReadonlyURLSearchParams,
    staticsData: ProductsItem[],
    user?: UserType,
) {
    const [error, setError] = useState<EligibilityError>();
    const [currentStep, setCurrentStep] = useState<ELIGIBILITY_STEP_NAME>(ELIGIBILITY_STEP_NAME.SINGLE_SEARCH_ELIG);
    const [currentStepStatus, setCurrentStepStatus] = useState<ELIGIBILITY_STEP_STATUS>(ELIGIBILITY_STEP_STATUS.VALID);
    const [isFirstStep, setIsFirstStep] = useState<boolean>(true);
    const [isMapFirstStep, setIsMapFirstStep] = useState<boolean>(false);
    const [address, setAddress] = useState<EligibilityAddress>();
    const [results, setResults] = useState<EligibilityResults>();
    const [shouldShowBasePrice, setShouldShowBasePrice] = useState(user !== null);
    const [marketing, setMarketing] = useMarketings();
    const [upsellModal, setUpsellModal] = useState<UpsellModalContainerProps | null>(null);

    const shortList: ShortListContext | undefined = useMemo(() => {
        const gamme = searchParams.get('gamme');
        const focus = searchParams.get('focus');

        if (gamme || focus) {
            const { rangeNgCommercialSeries, technology } = splitShortListParams(gamme ?? focus ?? undefined);
            const name = getShortListName(
                rangeNgCommercialSeries,
                technology?.toUpperCase() as TECHNOLOGY,
                staticsData,
            );

            if (technology && name) {
                return {
                    name,
                    technology,
                    focus: !!focus,
                    rangeNgCommercialSeries,
                };
            }
        }
    }, [searchParams]);

    const _setAddress = (value: IRudiApiRechercherAdresse) => {
        const newAddress = new EligibilityAddress(value);
        setAddress(newAddress);
    };

    const openOfferUpsellModal = (
        clickedOffer: EligibilityOffer,
        upsellOffer: EligibilityOffer,
        modal?: UpsellModalComponentType,
    ): void => {
        sendUpsellModalOpen(journeyConfig.tracking);

        const onClose = () => {
            sendUpsellModalClose(journeyConfig.tracking);
            setUpsellModal(null);
        };

        const onAddOffer = async (_offer: FaiType | EligibilityOffer) => {
            const offer = _offer as EligibilityOffer;

            sendUpsellModalClick(journeyConfig.tracking, offer.rangeNg + offer.commercialSeries);
            const autoAdd = offer?.neededElements?.map((el) => el.gencode) ?? [];
            void addToCart([offer.sku, ...autoAdd], true, () => setUpsellModal(null));
        };

        if (modal) {
            setUpsellModal({ type: modal, clickedOffer, upsellOffer, onClose, onAddOffer });
        }
    };

    const handleSkipToCart = async (_results: EligibilityResults, shortList: ShortListContext): Promise<boolean> => {
        // Retrieve offers for the technology specified in shortList
        const offers = _results.offers[normalizeTechnology(shortList.technology)];

        if (offers) {
            // Get the targeted offer by prioritizing 3P offers
            const offer = offers
                .filter(
                    (offer) =>
                        offer.rangeNg + '_' + offer.commercialSeries === shortList?.rangeNgCommercialSeries ||
                        offer.range === shortList?.rangeNgCommercialSeries,
                )
                .sort((offer1, offer2) => {
                    const isFirstOffer3P = offer1.play === PLAY._3P;
                    const isSecondOffer3P = offer2.play === PLAY._3P;

                    if (isFirstOffer3P && !isSecondOffer3P) {
                        return -1; // offer1 comes first
                    } else if (!isFirstOffer3P && isSecondOffer3P) {
                        return 1; // offer2 comes first
                    }
                    return 0; // No change in order
                })[0];

            if (offer) {
                // Check if the offer is upsellable
                const upsellData = journeyConfig.productsToUpsell.find(({ baseProduct }) => {
                    return offer.isSameRangeAndPlay(
                        baseProduct.rangeNg,
                        baseProduct.commercialSeries,
                        baseProduct.play,
                    );
                });

                if (upsellData) {
                    const upsellOffer = offers.find(
                        (item) =>
                            upsellData.upselledProduct &&
                            item.isSameRangeAndPlay(
                                upsellData.upselledProduct?.rangeNg,
                                upsellData.upselledProduct?.commercialSeries,
                                upsellData.upselledProduct?.play,
                            ),
                    );

                    if (upsellOffer && upsellData.modalComponentType) {
                        openOfferUpsellModal(offer, upsellOffer, upsellData.modalComponentType);
                        setResults(_results);
                        return false;
                    }
                }

                // If no upsell, add the offer to the cart
                const autoAdd = offer?.neededElements?.map((el) => el.gencode) ?? [];
                console.log('Adding to cart:', [offer.sku, ...autoAdd]);
                await addToCart([offer.sku, ...autoAdd], true);
                console.log('Return 2');
                return true;
            }
        }
        console.log('Return 3');
        return false;
    };

    const _setResults = async (type: TYPE_ELIGIBILITY, result: EligibilityResultType) => {
        new EligibilityResults(
            type,
            result,
            {
                eligibilityConfig,
                journeyConfig,
            },
            user,
            async (newResults) => {
                // If "direct panier"
                console.log(
                    'should skip to cart',
                    newResults,
                    shortList,
                    shortList?.focus === false && (await handleSkipToCart(newResults, shortList)),
                );
                if (newResults && shortList?.focus === false && (await handleSkipToCart(newResults, shortList))) {
                    return;
                }
                setResult(newResults);
            },
        );
    };

    const setResult = (result: EligibilityResults) => {
        //set 'home' promoConfigurator to false if not FTTH
        if (!result.technology?.includes(TECHNOLOGY.FTTH)) {
            const discountConfigurator = setMarketing({ [MarketingType.HOME]: false });

            emitPriceContextChangeEvent(discountConfigurator);
        }
        setResults(result);
    };

    const onResetAddress = () => {
        EligibilityResults.clear();
        setError(undefined);
        setResults(undefined);
        setCurrentStep(ELIGIBILITY_STEP_NAME.SINGLE_SEARCH_ELIG);
        setCurrentStepStatus(ELIGIBILITY_STEP_STATUS.VALID);
        setIsFirstStep(true);
        setIsMapFirstStep(false);
        setAddress(undefined);
    };

    const handleStepStatusChange = (status: ELIGIBILITY_STEP_STATUS) => {
        if (status === ELIGIBILITY_STEP_STATUS.RESET) {
            onResetAddress();
        } else {
            setCurrentStepStatus(status);
        }
    };

    const handleStepChange = (event: WetoStepChangeEvent['event']) => {
        setCurrentStep(event.currentStep.name);
        setCurrentStepStatus(ELIGIBILITY_STEP_STATUS.INVALID);
        setIsFirstStep(event.history.length === 0);
    };

    const handleEligibilityChange = (step: ELIGIBILITY_STEP_NAME) => {
        setCurrentStep(step);
        setCurrentStepStatus(ELIGIBILITY_STEP_STATUS.INVALID);
    };

    useEffect(() => {
        async function getResultFromStorage() {
            EligibilityResults.fromLocalStorage(
                {
                    eligibilityConfig,
                    journeyConfig,
                },
                user,
                async (newResults) => {
                    if (newResults && shortList?.focus === false) {
                        const shouldDisplayOffers = !(await handleSkipToCart(newResults, shortList));
                        if (!shouldDisplayOffers) {
                            return;
                        }
                    }

                    setResults(newResults);
                },
            );
        }

        getResultFromStorage().catch(() => {
            EligibilityResults.clear();
        });
    }, []);

    useEffect(() => {
        if (user) {
            setShouldShowBasePrice(true);
        } else {
            setShouldShowBasePrice(!marketing[MarketingType.HOME]);
        }
    }, [user]);

    return {
        error,
        setError,
        currentStep,
        setCurrentStep,
        currentStepStatus,
        setCurrentStepStatus,
        isFirstStep,
        setIsFirstStep,
        isMapFirstStep,
        setIsMapFirstStep,
        address,
        setAddress: _setAddress,
        results,
        setResults: _setResults,
        onResetAddress,
        shouldShowBasePrice,
        upsellModal,
        openOfferUpsellModal,
        shortList,
        handleStepStatusChange,
        handleEligibilityChange,
        handleStepChange,
    };
}
